import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { faPencil, faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import { Popup } from '../../../core/shared/components/popup/popup';
import { Setup } from '../../setup/shared/service/setup';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { Table } from '../../shared/table/table';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { PaymentTypeFormModel } from '../../../core/shared/models/paymenttypemodel';
import { of } from 'rxjs';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { ToastService } from '../../../core/shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';




export interface PaymentTypeUI {
  typSeq: number;
  typId: number;
  typStr: string;
  typStsKey: number;
  prdSeq: number;
  glAcctNum: string;
}

@Component({
  selector: 'app-remittance-type',
  imports: [CommonModule, FormsModule, Table, Popup, TranslatePipe],
  templateUrl: './remittance-type.html',
  styleUrl: './remittance-type.css',
})
export class RemittanceType implements OnInit {
  faPencil = faPencil;
  faTrash = faTrash;
  faSync = faSync;

  data: PaymentTypeUI[] = [];
  selectedRow: PaymentTypeUI | null = null;
  showPopup = false;
  popupTitle = '';
  isDeleteConfirm = false;
  popupFields: DynamicFieldConfig[] = [];

  remitanceType = 3;
  productOptions: { label: string, value: number }[] = [];
  productKey = 14;

  constructor(
    private setupService: Setup,
    private pickListService: PickListService,
    private toast: ToastService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadPaymentTypes();
    this.loadProducts();
  }

loadPaymentTypes(): void {
  this.setupService.getTypes(3).subscribe({
    next: (res: PaymentTypeUI[]) => {
      this.data = res.map(item => ({
        typSeq: item.typSeq,
        typId: item.typId,
        typStr: item.typStr || '',
        typStsKey: item.typStsKey || 0,
        prdSeq: item.prdSeq || 0,
        glAcctNum: item.glAcctNum || '',
        status: item.typStsKey === 1 ? 'Active' : 'Inactive'
      }));

      // ✅ Sort: Letters first (A-Z), then numbers — by Remittance Type
      this.data.sort((a, b) => {
        const nameA = (a.typStr || '').trim();
        const nameB = (b.typStr || '').trim();

        const aStartsWithNumber = /^\d/.test(nameA);
        const bStartsWithNumber = /^\d/.test(nameB);

        if (aStartsWithNumber && !bStartsWithNumber) return 1;
        if (!aStartsWithNumber && bStartsWithNumber) return -1;

        return nameA.localeCompare(nameB, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      });
    },
    error: err => console.error(err)
  });
}

  loadProducts(): void {
    this.setupService.getTypes(this.productKey).subscribe({
      next: (res: any) => {
        const list = res?.data ?? [];
        this.productOptions = list.map((p: any) => ({
          label: p.productName,
          value: p.productId
        }));
      },
      error: err => console.error(err)
    });
  }

  columns: TableColumn[] = [
    { key: 'typSeq', label: 'GL.PAYMENT_TYPE.ID' },
    { key: 'typStr', label: 'GL.PAYMENT_TYPE.NAME' },
    { key: 'glAcctNum', label: 'GL.GL_ACCOUNT' },
    { key: 'status', label: 'COMMON.STATUS', type: 'map', map: { Active: 'COMMON.ACTIVE', Inactive: 'COMMON.INACTIVE' } }
  ];

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-green-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: PaymentTypeUI) => this.openEditPopup(row)
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: PaymentTypeUI) => this.openDeletePopup(row)
    },
    {
      icon: this.faSync,
      class: 'text-black',
      tooltip: 'COMMON.REFRESH',
      onClick: () => this.loadPaymentTypes()
    }
  ];

  openAddPopup(): void {
    this.selectedRow = null;
    this.isDeleteConfirm = false;
    this.popupTitle = 'GL.REMITTANCE_TYPE.ADD';
    this.popupFields = [
      { key: 'typStr', label: 'GL.REMITTANCE_TYPE.NAME', type: 'text', validations: { required: true } },
      { key: 'glAcctNum', label: 'GL.GL_ACCOUNT', type: 'text', validations: { required: true } },
      {
        key: 'typStsKey', label: 'COMMON.STATUS', type: 'select', value: 1, options$: [
          { label: 'COMMON.ACTIVE', value: 1 },
          { label: 'COMMON.INACTIVE', value: 0 }
        ]
      }
    ];
    this.showPopup = true;
  }

  openEditPopup(row: PaymentTypeUI): void {
    this.selectedRow = row;
    this.isDeleteConfirm = false;
    this.popupTitle = 'GL.REMITTANCE_TYPE.EDIT';
    this.popupFields = [
      { key: 'typStr', label: 'GL.REMITTANCE_TYPE.NAME', type: 'text', value: row.typStr, validations: { required: true } },
      { key: 'glAcctNum', label: 'GL.GL_ACCOUNT', type: 'text', value: row.glAcctNum, validations: { required: true } },
      {
        key: 'typStsKey', label: 'COMMON.STATUS', type: 'select', value: row.typStsKey, options$: [
          { label: 'COMMON.ACTIVE', value: 1 },
          { label: 'COMMON.INACTIVE', value: 0 }
        ]
      }
    ];
    this.showPopup = true;
  }

  openDeletePopup(row: PaymentTypeUI): void {
    this.selectedRow = row;
    this.isDeleteConfirm = true;
    this.popupTitle = 'GL.REMITTANCE_TYPE.DELETE';
    this.popupFields = [
      { label: this.translate.instant('GL.REMITTANCE_TYPE.DELETE_CONFIRM', { id: row.typSeq }), key: 'confirm', type: 'message' }
    ];
    this.showPopup = true;
  }

  handleSave(formData: any): void {
    const isDelete = this.isDeleteConfirm && this.selectedRow;

    const payload = {
      typSeq: this.selectedRow ? this.selectedRow.typSeq : null,
      typId: this.selectedRow ? this.selectedRow.typId : 0,
      typStr: isDelete
        ? this.selectedRow?.typStr
        : formData.typStr,

      prdSeq: isDelete
        ? this.selectedRow?.prdSeq
        : formData.prdSeq,

      glAcctNum: isDelete
        ? this.selectedRow?.glAcctNum   // ✅ 'this.selectedRow' instead of 'selectedRow'
        : formData.glAcctNum,

      typStsKey: isDelete
        ? this.selectedRow?.typStsKey   // ✅ 'this.selectedRow' instead of 'selectedRow'
        : formData.typStsKey,

      typCtgryKey: 3, // remittance
      delFlg: isDelete ? 1 : 0
    };

    this.setupService.savetype(payload).subscribe({
      next: () => {
        if (isDelete) this.toast.success('GL.DELETED_SUCCESSFULLY');
        else if (this.selectedRow) this.toast.success('GL.UPDATED_SUCCESSFULLY');
        else this.toast.success('GL.SAVED_SUCCESSFULLY');

        this.showPopup = false;
        this.loadPaymentTypes();
      },
      error: err => {
        console.error('Operation failed', err);
        this.toast.error('GL.OPERATION_FAILED');
      }
    });
  }
}